import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { COMPLIANCE_LABELS, INDUSTRIES, getPlan } from '@shared/catalog'
import { INDUSTRY_BLUEPRINTS, blueprintSummary } from '@shared/blueprint'
import type { IndustryId } from '@shared/types'
import { MarketingShell, DemoStartError } from '../components/Layout'
import { HeroRibbon } from '../components/HeroRibbon'
import { Card, Chip } from '../ui'
import { api } from '../lib/api'

/**
 * One hand-written line per industry — everything else on this page (labels, quick replies,
 * FAQs, SLA minutes, compliance chips) is pulled from `INDUSTRY_BLUEPRINTS`/`INDUSTRIES`,
 * the same seed data a real workspace gets applied to its own database at provision time.
 * That is deliberate: a quick reply shown here is one an operator in that sector will actually
 * see on day one, not marketing copy invented for this page.
 */
const HEADLINES: Record<IndustryId, string> = {
  general: 'One inbox for every conversation your business has',
  ecommerce: 'Answer every order question before the cart goes cold',
  education: 'Enrolment, trial bookings, and parent updates in one thread',
  healthcare: 'Patient scheduling on WhatsApp, without the compliance guesswork',
  financial: 'Advisory conversations your compliance team can actually review',
  field_services: 'Dispatch and job updates your technicians will actually use',
  logistics: "Fleet coordination that doesn't live in a driver's group chat",
}

export function IndustryLanding() {
  const { industryId } = useParams({ from: '/industries/$industryId' })
  const navigate = useNavigate()
  const startDemo = useMutation({
    mutationFn: api.startDemo,
    onSuccess: () => navigate({ to: '/app' }),
  })

  const industry = INDUSTRIES.find((i) => i.id === industryId)
  if (!industry) {
    return (
      <MarketingShell>
        <section className="container section stack gap-4" style={{ paddingBlock: '56px' }}>
          <h1 className="display-small">No such industry</h1>
          <p className="body-large text-muted">
            <Link to="/industries">See the full list of industries.</Link>
          </p>
        </section>
      </MarketingShell>
    )
  }

  const blueprint = INDUSTRY_BLUEPRINTS[industry.id]
  const summary = blueprintSummary(industry.id)
  const plan = getPlan(industry.presets.plan)!
  const compliance = industry.compliance.filter((c) => c !== 'none')

  return (
    <MarketingShell>
      <section
        className="hero"
        style={{ paddingBlock: 'clamp(48px, 9vw, 104px)', minHeight: 'min(58vh, 560px)' }}
      >
        <HeroRibbon />
        <div className="hero-veil" />
        <div className="container stack gap-5">
          <div className="row gap-2 wrap">
            <Chip tone="assist">{industry.name}</Chip>
            {compliance.map((c) => (
              <Chip key={c} tone="warn">
                {COMPLIANCE_LABELS[c]}
              </Chip>
            ))}
          </div>
          <h1 className="display-large" style={{ maxWidth: '18ch' }}>
            {HEADLINES[industry.id]}
          </h1>
          <p className="body-large text-muted measure">{industry.description}</p>
          <div className="row gap-3 wrap" style={{ marginTop: '8px' }}>
            <Link to="/signup" search={{ industry: industry.id }} className="btn btn-filled btn-large">
              Start 14-day free trial
            </Link>
            <button
              type="button"
              className="btn btn-outlined btn-large"
              onClick={() => startDemo.mutate()}
              disabled={startDemo.isPending}
            >
              {startDemo.isPending ? 'Loading demo…' : 'See a live demo'}
            </button>
          </div>
          {startDemo.isError && <DemoStartError error={startDemo.error} />}
        </div>
      </section>

      <section className="section rule-top" style={{ background: 'var(--md-surface-container-low)' }}>
        <div className="container stack gap-2">
          <span className="eyebrow">Already set up when you sign up</span>
          <div className="row gap-2 wrap" style={{ marginTop: '4px' }}>
            <Chip tone="assist">{summary.labels} chat labels</Chip>
            <Chip tone="assist">{summary.quickReplies} quick replies</Chip>
            <Chip tone="assist">{summary.knowledgeAnswers} FAQ answers</Chip>
            <Chip tone="assist">{summary.rules} workflow rules</Chip>
            <Chip tone="assist">Fastest SLA: {summary.tightestSlaMinutes} min</Chip>
            {summary.mandatoryTicketFields > 0 && (
              <Chip tone="assist">
                {summary.mandatoryTicketFields} field{summary.mandatoryTicketFields === 1 ? '' : 's'} required before close
              </Chip>
            )}
          </div>
          <p className="body-medium text-muted" style={{ maxWidth: '62ch', marginTop: '8px' }}>
            {industry.presetRationale}
          </p>
        </div>
      </section>

      {blueprint.quickReplies.length > 0 && (
        <section className="container section stack gap-5">
          <div className="stack gap-2">
            <span className="eyebrow">Ready to send</span>
            <h2 className="headline-medium" style={{ maxWidth: '24ch' }}>
              Canned replies written for {industry.name.toLowerCase()}
            </h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '16px',
            }}
          >
            {blueprint.quickReplies.slice(0, 4).map((reply) => (
              <Card key={reply.shortcut} variant="outlined">
                <div className="stack gap-2">
                  <div className="row gap-2 items-baseline">
                    <span className="label-medium" style={{ fontFamily: 'monospace' }}>
                      {reply.shortcut}
                    </span>
                    <span className="title-small">{reply.title}</span>
                  </div>
                  <p className="body-medium text-muted">{reply.body}</p>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {blueprint.knowledgeAnswers.length > 0 && (
        <section className="section rule-top" style={{ background: 'var(--md-surface-container-low)' }}>
          <div className="container stack gap-5">
            <div className="stack gap-2">
              <span className="eyebrow">Already answered</span>
              <h2 className="headline-medium" style={{ maxWidth: '28ch' }}>
                Starter answers for what customers actually ask
              </h2>
              <p className="body-medium text-muted" style={{ maxWidth: '62ch' }}>
                Generic on purpose — every one needs your own detail before it says something true
                about your business, but auto-reply has something to work with from day one.
              </p>
            </div>
            <div className="stack gap-3" style={{ maxWidth: '75ch' }}>
              {blueprint.knowledgeAnswers.slice(0, 3).map((answer) => (
                <Card key={answer.title} variant="outlined">
                  <div className="stack gap-1">
                    <h3 className="title-medium">{answer.title}</h3>
                    <p className="body-medium text-muted">{answer.body}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="container section stack gap-6">
        <div className="stack gap-2">
          <span className="eyebrow">Where this starts</span>
          <h2 className="headline-medium">The plan and modules this sector runs on</h2>
        </div>
        <Card variant="outlined">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(200px, auto)',
              gap: '24px',
              alignItems: 'center',
            }}
          >
            <div className="stack gap-2">
              <span className="label-medium text-muted">Starting plan</span>
              <span className="title-large">{plan.name}</span>
              <div className="row gap-2 wrap" style={{ marginTop: '4px' }}>
                {industry.workflowTemplates.map((w) => (
                  <Chip key={w} tone="assist">
                    {w}
                  </Chip>
                ))}
              </div>
            </div>
            <Link to="/pricing" search={{ industry: industry.id }} className="btn btn-tonal btn-large">
              Price this configuration
            </Link>
          </div>
        </Card>
      </section>

      <section className="container section stack gap-4 center" style={{ textAlign: 'center', alignItems: 'center' }}>
        <h2 className="headline-large" style={{ maxWidth: '20ch' }}>
          See it running with your own numbers
        </h2>
        <p className="body-large text-muted" style={{ maxWidth: '52ch' }}>
          No card required. No WhatsApp Business API approval, no template queue — or look at a
          live demo first.
        </p>
        <div className="row gap-3 wrap center">
          <Link to="/signup" search={{ industry: industry.id }} className="btn btn-filled btn-large">
            Start free trial
          </Link>
          <Link to="/industries" className="btn btn-text btn-large">
            See every industry
          </Link>
        </div>
      </section>
    </MarketingShell>
  )
}
